import { MutableRefObject, useEffect, useMemo, useRef } from 'react';

const useOnClickOutside = (
	ref: MutableRefObject<Element | null>,
	handler: (event: Event) => void,
	when = true,
	mouseEvent = 'click'
) => {
	const refs = useMemo(() => [ref], [ref]);
	return useOnClickOutsideMultipleRefs(refs, handler, when, mouseEvent);
};

export const useOnClickOutsideMultipleRefs = (
	refs: Array<MutableRefObject<Element | null>>,
	handler: (event: Event) => void,
	when = true,
	mouseEvent = 'click'
) => {
	const handlerRef = useRef(handler);
	useEffect(() => {
		handlerRef.current = handler;
	}, [handler]);

	useEffect(() => {
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				handlerRef.current(event);
			}
		};
		const listener = (event: Event) => {
			if (!handlerRef.current) {
				return;
			}
			const containedInRefs = refs
				.filter((ref): ref is MutableRefObject<Element> => !!ref.current)
				.some(ref => ref.current.contains(event.target as Node));
			if (containedInRefs) {
				return;
			}
			handlerRef.current(event);
		};

		const timeout = setTimeout(() => {
			if (when) {
				window.addEventListener('keydown', onKeyDown);
				window.addEventListener(mouseEvent, listener);
			}
		}, 0);

		return () => {
			clearTimeout(timeout);
			window.removeEventListener('keydown', onKeyDown);
			window.removeEventListener(mouseEvent, listener);
		};
	}, [handlerRef, refs, when, mouseEvent]);
};

export default useOnClickOutside;
